/* eslint-disable @typescript-eslint/no-empty-function */
import ls from 'localstorage-slim';
import { Signal } from 'typed-signals';
import type { AdInfo, XPromoSwitchGameBasePayload, AdError } from '@softgames/ad-flow';

import type { LoaderParser } from 'pixi.js';
import type {
  PlatformUser,
  Platform,
  PlatformTrackingConfig,
  PlatformPrivacyConsent,
  ForceUpdate,
  RateUs,
  PlatformCheatOptions,
  PlatformNotification,
  LocalNotifications,
  PlatformRemoteConfig,
  PlatformAdModule,
} from '@hexa/app/platform/Platform';
import { type AppState } from '@hexa/app/state/AppState';
import type { TrackingModule, TrackingModuleFactory } from '@hexa/app/tracking/types';
import type { FallbackControllerContext } from '@hexa/app/ads/FallbackOfferContext';
import type { XPromoAdContext } from '@hexa/app/ads/XPromoAdContext';
import facebookAvatarParser from '@hexa/app/platform/facebook/FacebookAvatarParser';
import { LanguageKey } from '@softgames/localization';
import { AzerionBridge } from '@softgames/azerion-bridge';
import { OverlayManager } from '@softgames/overlay-manager';
import { soundManager } from '@hexa/app/SoundManager';
import { AzerionAds } from './AzerionAds';
import { installGdAdEventObserver } from './gdAdEventObserver';
import { WebRemoteConfig } from './firebase/WebRemoteConfig';
import { WebCheatOptions } from './WebCheatOptions';
import { WebSafeArea } from './WebSafeArea';
import { FakeTournaments } from './FakeTournaments';
import { FakeLeaderboards } from './FakeLeaderboards';

export class PlatformAzerion implements Platform {
  public readonly name = 'Web';

  public adModule: AzerionAds | null = null;

  public readonly leaderboards = new FakeLeaderboards();

  public readonly social: null = null;

  public readonly tournaments: FakeTournaments | null;

  public readonly shareable: null = null;

  public readonly payments: null = null;

  public readonly deviceSafeArea = new WebSafeArea();

  public readonly onAdFinished = new Signal<(properties: { adInfo: AdInfo; adError: AdError | null }) => unknown>();

  public readonly onCheatMenuOptionsChanged: Signal<(enabled: boolean) => void> = new Signal<
    (enabled: boolean) => void
  >();

  private readonly multiplatformPlayerId: string | null = null;

  public readonly privacyConsent: PlatformPrivacyConsent | null = null;

  public readonly forceUpdate: ForceUpdate | null = null;

  public readonly rateUs: RateUs | null = null;

  public readonly cheatOptions: PlatformCheatOptions = new WebCheatOptions();

  public readonly notification: PlatformNotification | null = null;

  public readonly localNotifications: LocalNotifications | null = null;

  public readonly platformRemoteConfig: PlatformRemoteConfig = new WebRemoteConfig();

  private readonly bridge: AzerionBridge | null = null;

  private readonly loadingScreen: OverlayManager;

  constructor() {
    this.tournaments = new FakeTournaments();

    this.bridge = new AzerionBridge({
      gameId: 'ac3276e1819644858f0c8693c219064c',
      gameSlug: 'hexa-stack',
      gameVersion: APP_VERSION,
      adsConfig: {
        adFreeInitialLevels: 0,
        bannerAds: false,
      },
      isDev: ENVIRONMENT === 'dev',
      disablePreroll: true,
    });

    this.loadingScreen = new OverlayManager({
      id: 'loadingScreenOverlay',
      iconUri: './Icon_512x512.png',
      logoUri: 'https://assets.gd.sbs.softgames.de/assets/wrapper_images/softgames_logo.png',
      styles: {
        overlayLogo: {
          //eslint-disable-next-line @typescript-eslint/naming-convention
          'max-width': '50%',
          position: 'relative',
        },
      },
    });
    this.loadingScreen.show();
  }

  public getPlatformType(): string {
    return 'WEB';
  }

  /** AZ-998: the long-play timer is the only automatic interstitial on Azerion. */
  public disableAutomaticInterstitials(): boolean {
    return true;
  }

  public getSupportEmail(): string | null {
    return null;
  }

  public getVersionInfo(): string | null {
    return TRACKING_VERSION;
  }

  public async init(): Promise<void> {
    if (this.bridge) {
      // Must run before bridge.init() assigns window.GD_OPTIONS / loads the GD SDK,
      // so our onEvent wrapper is the one the SDK captures.
      installGdAdEventObserver();
      await this.bridge.init();
    }

    this.overrideVisibilityChange();
  }

  public async createShortcut(): Promise<boolean> {
    return true;
  }

  public async startGame(): Promise<void> {
    this.loadingScreen.hide();
    // AZ-998: no GameStart emit — the scheduler counts only active gameplay.
    this.adModule?.setupPrerollAd();
  }

  public setLoadingProgress(): void {}

  public getCurrentPlayer(): PlatformUser {
    return {
      id: this.multiplatformPlayerId ?? 'local_user',
      avatarUrl:
        'https://platform-lookaside.fbsbx.com/platform/profilepic/?asid=1931967203912562&gaming_photo_type=unified_picture&ext=1723365435&hash=AbYr0FxQKRPPGmxT4BeVSuXc',
      currentUser: true,
      name: 'You',
    };
  }

  public getFriends(): Promise<PlatformUser[]> {
    return Promise.resolve([]);
  }

  public async getEntryPointName(): Promise<string | null> {
    // TODO-AZERION
    return null;
  }

  public getEntryPointData(): null {
    return null;
  }

  public async saveStateAsync(data: AppState): Promise<void> {
    ls.set(`${APP_NAME}State`, data);
  }

  public async flushDataAsync(): Promise<void> {
    await Promise.resolve();
  }

  public async loadStateAsync(): Promise<Partial<AppState>> {
    return Promise.resolve(ls.get(`${APP_NAME}State`) ?? {});
  }

  public logEvent(_eventName: string, _value?: number, _parameters?: Record<string, string>): void {}

  public async checkBotSubscription(): Promise<void> {
    await Promise.resolve();
  }

  public getTrackingConfig(): PlatformTrackingConfig | null {
    return null;
  }

  public getAvatarParser(): LoaderParser | null {
    // Reusing facebook avatar parser, as the placeholder is from FB server
    return facebookAvatarParser;
  }

  private isDev(): boolean {
    return ENVIRONMENT === 'dev';
  }

  public setTrackingModule(_trackingModule: TrackingModule): void {
    // Not implemented
  }

  public addWindowFocusChangeCallback(_callback: (focus: boolean) => void): void {}

  public removeWindowFocusChangeCallback(_callback: (focus: boolean) => void): void {
    // Not implemented
  }

  public canUseCheats(): boolean {
    return true;
  }

  public tryEnableCheatMenu(): void {}

  public cheatEnabled(): boolean {
    if (this.isDev()) {
      return true;
    }
    return false;
  }

  public async setUserSegmentation(_sessionCount: number): Promise<void> {
    // Web platform doesn't support Firebase user properties
  }

  public switchGame(_: XPromoSwitchGameBasePayload): Promise<void> {
    return Promise.resolve();
  }

  public async showBannerAd(): Promise<void> {
    // Not implemented
  }

  public async hideBannerAd(): Promise<void> {
    // Not implemented
  }

  public createAdsModule(
    _fallbackControllerContext: FallbackControllerContext,
    _xPromoContext: XPromoAdContext,
  ): PlatformAdModule {
    if (this.adModule) {
      return this.adModule;
    }

    if (!this.bridge) {
      throw new Error('Azerion Bridge is not initialized');
    }

    this.adModule = new AzerionAds(this.bridge);

    return this.adModule;
  }

  public getTrackingModuleFactory(): TrackingModuleFactory | null {
    return null;
  }

  public getBannerHeight(): number {
    return 0;
  }

  public getSupportedLanguages(): LanguageKey[] {
    if (this.bridge?.user.getLanguage() === 'ja') {
      return [LanguageKey.JAPANESE_JP];
    }

    return [
      LanguageKey.ENGLISH_US,
      LanguageKey.FRENCH_FR,
      LanguageKey.ITALIAN_IT,
      LanguageKey.GERMAN_DE,
      LanguageKey.POLISH_PL,
      LanguageKey.DUTCH_NL,
    ];
  }

  private overrideVisibilityChange(): void {
    soundManager.removeEventListener('visibilitychange');
    soundManager.addEventListener('visibilitychange', () => {
      if (this.adModule?.isAdShowing) return;

      if (document.hidden) {
        soundManager.mute();
      } else {
        soundManager.unmute();
      }
    });
  }
}
